import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import { ArrowLeft, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  GenericCariModal,
  type GenericCariType,
} from '@/components/trading/cari/generic-cari-modal';
import { BasicCariDetailContent } from '@/components/trading/cari/basic-cari-detail-content';
import {
  cariTypeDisplay,
  cariTypeLabel,
  isGenericCariType,
} from '@/components/trading/cari/cari-type-display';
import { trpc } from '@/lib/trpc';

export function GenericCariDetailPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const { t, i18n } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('CUSTOMERS', 'EDIT', 'TRADING');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const cariType = (type ?? '') as GenericCariType;
  // Any registry code except the dedicated M/T/S/B pages is handled here.
  const isValidType = !!cariType && isGenericCariType(cariType);

  const { data: supplier, isLoading, error } = trpc.tradingSupplier.byId.useQuery(
    id!,
    { enabled: !!id && isValidType }
  );
  const { data: cariTypes } = trpc.tradingCariType.list.useQuery();
  const typeRow = cariTypes?.find((tp) => tp.code === cariType);
  const typeLabel = typeRow ? cariTypeLabel(typeRow, i18n.language) : undefined;

  if (!isValidType) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/cari')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('cari.title')}
        </Button>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">Invalid cari type: {type}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(`/trading/cari?type=${cariType}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('cari.title')}
        </Button>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || tCommon('notFound')}</p>
        </div>
      </div>
    );
  }

  const iconConfig = cariTypeDisplay(cariType);
  const Icon = iconConfig.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/trading/cari?type=${cariType}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('cari.title')}
          </Button>
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center ${iconConfig.bgColor}`}>
              <Icon className={`h-5 w-5 ${iconConfig.iconColor}`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{supplier.companyName}</h1>
              {supplier.tradeName && (
                <p className="text-muted-foreground">{supplier.tradeName}</p>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {tCommon('edit')}
          </Button>
        )}
      </div>

      <GenericCariModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        cariType={cariType}
        typeLabel={typeLabel}
        cari={{
          id: supplier.id,
          companyName: supplier.companyName,
          contactName: supplier.contactName,
          email: supplier.email,
          phone: supplier.phone,
          website: supplier.website,
          address: supplier.addressLine1,
          city: supplier.city,
          country: supplier.country,
          taxId: supplier.taxId,
          paymentTerms: supplier.paymentTerms as any,
          notes: supplier.notes,
          isActive: supplier.isActive,
        }}
      />

      <BasicCariDetailContent cariType={cariType} supplier={supplier} typeLabel={typeLabel} />
    </div>
  );
}
